import { z } from 'zod';

const recipientList = z.array(z.email('Ongeldig e-mailadres'));

export const translatorProviderSchema = z.enum(['azure', 'google']);
export type TranslatorProvider = z.infer<typeof translatorProviderSchema>;

const settingsFields = {
  articleNumbers: z.array(z.string()),
  thankYouUrlNl: z.string().url('Ongeldige URL').nullable().optional(),
  thankYouUrlFr: z.string().url('Ongeldige URL').nullable().optional(),
  thankYouUrlEn: z.string().url('Ongeldige URL').nullable().optional(),
  luxuryIntakeRecipients: recipientList,
  projectIntakeRecipients: recipientList,
  b2bSalesSheetId: z.string().trim().max(255),
  luxurySalesSheetId: z.string().trim().max(255),
  registrationExportDriveFolderId: z.string().trim().max(255),
  smtpHost: z.string().trim().max(255),
  smtpPort: z.coerce.number().int().min(1).max(65_535),
  smtpSecure: z.boolean(),
  smtpUsername: z.string().trim().max(255),
  smtpPassword: z.string().max(1_000).optional(),
  clearSmtpPassword: z.boolean().optional(),
  smtpFrom: z.union([z.literal(''), z.email('Ongeldig e-mailadres')]),
  translatorProvider: translatorProviderSchema,
  azureTranslatorKey: z.string().max(1_000).optional(),
  clearAzureTranslatorKey: z.boolean().optional(),
  azureTranslatorRegion: z.string().trim().max(255),
  azureTranslatorEndpoint: z.union([z.literal(''), z.url('Ongeldige Azure Translator-URL')]),
  googleTranslatorKey: z.string().max(1_000).optional(),
  clearGoogleTranslatorKey: z.boolean().optional(),
};

export const settingsSchema = z
  .object({
    ...settingsFields,
    articleNumbers: settingsFields.articleNumbers.default([]),
    luxuryIntakeRecipients: settingsFields.luxuryIntakeRecipients.default([]),
    projectIntakeRecipients: settingsFields.projectIntakeRecipients.default([]),
    b2bSalesSheetId: settingsFields.b2bSalesSheetId.default(''),
    luxurySalesSheetId: settingsFields.luxurySalesSheetId.default(''),
    registrationExportDriveFolderId: settingsFields.registrationExportDriveFolderId.default(''),
    smtpHost: settingsFields.smtpHost.default(''),
    smtpPort: settingsFields.smtpPort.default(587),
    smtpSecure: settingsFields.smtpSecure.default(false),
    smtpUsername: settingsFields.smtpUsername.default(''),
    smtpFrom: settingsFields.smtpFrom.default(''),
    translatorProvider: settingsFields.translatorProvider.default('azure'),
    azureTranslatorRegion: settingsFields.azureTranslatorRegion.default(''),
    azureTranslatorEndpoint: settingsFields.azureTranslatorEndpoint.default(
      'https://api.cognitive.microsofttranslator.com'
    ),
  })
  .refine(data => !(data.smtpPassword && data.clearSmtpPassword), {
    message: 'Vul een nieuw wachtwoord in of verwijder het opgeslagen wachtwoord.',
    path: ['smtpPassword'],
  })
  .refine(data => !(data.azureTranslatorKey && data.clearAzureTranslatorKey), {
    message: 'Vul een nieuwe Azure-sleutel in of verwijder de opgeslagen sleutel.',
    path: ['azureTranslatorKey'],
  })
  .refine(data => !(data.googleTranslatorKey && data.clearGoogleTranslatorKey), {
    message: 'Vul een nieuwe Google-sleutel in of verwijder de opgeslagen sleutel.',
    path: ['googleTranslatorKey'],
  });

export const updateSettingsSchema = z
  .object(settingsFields)
  .partial()
  .refine(data => !(data.smtpPassword && data.clearSmtpPassword), {
    message: 'Vul een nieuw wachtwoord in of verwijder het opgeslagen wachtwoord.',
    path: ['smtpPassword'],
  })
  .refine(data => !(data.azureTranslatorKey && data.clearAzureTranslatorKey), {
    message: 'Vul een nieuwe Azure-sleutel in of verwijder de opgeslagen sleutel.',
    path: ['azureTranslatorKey'],
  })
  .refine(data => !(data.googleTranslatorKey && data.clearGoogleTranslatorKey), {
    message: 'Vul een nieuwe Google-sleutel in of verwijder de opgeslagen sleutel.',
    path: ['googleTranslatorKey'],
  });

export type SettingsFormData = z.infer<typeof settingsSchema>;
export type UpdateSettingsData = z.infer<typeof updateSettingsSchema>;
